import React, { useEffect, useState } from "react";

import { useTranslation } from "react-i18next";
import { FrontendUserInfo } from "node-library/dist/portal";
import {
	CommonSwitch,
	CustomRadioGroup,
	UserInfoTextInput,
} from "../../common-components/inputs";
import { Card, Divider } from "@mui/material";
import { NeutralColor, SecondaryColor } from "node-library/dist/colors";
import {
	CompanyIcon,
	DeleteIcon,
	InvoiceIcon,
	LockIcon,
	UserIcon,
} from "../../common-components/svgIcons";
import { useSnackbar } from "../../common-components/toast/showToastMessage";
import {
	FrontendProfileTab,
	Severity,
	SystemRole,
} from "node-library/dist/constants";
import { YellowWarnIcon } from "../../common-components/toast/warnIcons";
import {
	GreenButton,
	OutlinedButton,
	TextButton,
} from "../../common-components/button";

import ChangePasswordModal from "./components/ChangePasswordModal";
import {
	sendGETRequest,
	sendPOSTRequest,
} from "../../requestHelper/sendRequest";
import { RedErrorIcon } from "../../common-components/toast/errorIcons";
import UserClientInfoSection from "./userClientInfoSection";

import { GreenTickIcon } from "../../common-components/toast/successIcons";
import { ButtonTextLoadingAnimation } from "../../common-components/animation/ButtonLoadingAnimation";
import { validateEmail } from "../../helpers/inputHelper";
import { getUserRoleString } from "../../helpers/userHelper";

interface Props {
	user: FrontendUserInfo | undefined;
	setUser: React.Dispatch<React.SetStateAction<FrontendUserInfo | undefined>>;
	setUnsavedChanges: React.Dispatch<React.SetStateAction<boolean>>;
}

export default function UserProfilePage(props: Props) {
	const { t: translation } = useTranslation();
	const { openSnackbar } = useSnackbar();

	const user = props.user;
	const setUnsavedChanges = props.setUnsavedChanges;

	const [currentTab, setCurrentTab] = useState<FrontendProfileTab>(
		FrontendProfileTab.PROFILE
	);
	const [loading, setLoading] = useState<boolean>(false);

	const [firstName, setFirstName] = useState<string>(user?.firstName || "");
	const [lastName, setLastName] = useState<string>(user?.lastName || "");

	const [email, setEmail] = useState<string>(user?.email || "");

	// const [enableNotificationEmail, setEnableNotificationEmail] =
	// 	useState<boolean>(user?.enableNotificationEmail || false);

	//password change
	const [changePasswordModalOpen, setChangePasswordModalOpen] =
		useState<boolean>(false);

	//validations
	const [firstNameErrorMsg, setFirstNameErrorMsg] = useState<string>("");
	const [lastNameErrorMsg, setLastNameErrorMsg] = useState<string>("");
	const [emailErrorMsg, setEmailErrorMsg] = useState<string>("");

	//client address info

	function resetUserStatesToDefault(user: FrontendUserInfo) {
		setFirstName(user?.firstName || "");
		setLastName(user?.lastName || "");
	}

	function discardChanges() {
		resetUserStatesToDefault(props.user as FrontendUserInfo);
		setUnsavedChanges(false);
		openSnackbar(
			translation("ChangesDiscarded"),
			Severity.SUCCESS,
			undefined,
			<GreenTickIcon />
		);
	}

	const clientInformationFields: Array<any> = [
		{
			id: "profile-username-input",
			label: `${translation("Username")}`,
			inputType: "text",
			value: user?.userName,
			disabled: true,
			setErrorMsg: () => {},
		},
		{
			id: "profile-email-input",
			label: `${translation("Email")}`,
			inputType: "email",
			value: email,
			setValue: setEmail,
			required: true,
			errorMsg: emailErrorMsg,
			setErrorMsg: setEmailErrorMsg,
		},
		{
			id: "profile-firstname-input",

			label: `${translation("FirstName")}`,
			inputType: "text",
			value: firstName,
			setValue: setFirstName,
			required: true,
			errorMsg: firstNameErrorMsg,
			setErrorMsg: setFirstNameErrorMsg,
		},
		{
			id: "profile-lastname-input",

			label: `${translation("LastName")}`,
			inputType: "text",
			value: lastName,
			setValue: setLastName,
			required: true,
			errorMsg: lastNameErrorMsg,
			setErrorMsg: setLastNameErrorMsg,
		},
	];

	const handleSubmitUserInfo = async () => {
		let profilePageError: boolean = false;
		//validate profile
		for (const field of [...clientInformationFields]) {
			field.setErrorMsg("");
			if (
				field.required &&
				(!field.value || (field.value as string).trim().length == 0)
			) {
				field.setErrorMsg(translation("MissingInformation"));
				profilePageError = true;
			}
		}

		if (profilePageError) {
			setCurrentTab(FrontendProfileTab.PROFILE);
			return;
		}

		try {
			setLoading(true);
			const response = await sendPOSTRequest("/profile/update-user-profile", {
				firstName: firstName.trim(),
				lastName: lastName.trim(),
				email: email.trim(),

				// enableNotificationEmail: enableNotificationEmail,
			});
			if (response?.userInfo) {
				const userInfo: FrontendUserInfo = response.userInfo;
				localStorage.setItem(
					"billionspectra_lims_userInfo",
					JSON.stringify(response.userInfo)
				);
				props.setUser(userInfo);
				resetUserStatesToDefault(userInfo);
				setUnsavedChanges(false);

				openSnackbar(
					translation("InformationUpdated"),
					Severity.SUCCESS,
					undefined,
					<GreenTickIcon />
				);
			}
		} catch (err: any) {
			openSnackbar(
				translation("SystemError"),
				Severity.ERROR,
				translation("SystemErrorMsg"),
				<RedErrorIcon />
			);
		} finally {
			setLoading(false);
		}
	};

	const avatarPlaceholder: React.ReactNode = (
		<div className="profile-page-user-avatar">
			<div
				className="typography-heading-01"
				style={{ color: SecondaryColor.SECONDARY_200 }}
			>{`${user?.firstName.charAt(0)}${user?.lastName.charAt(0)}`}</div>
		</div>
	);

	const headerCard: React.ReactNode = (
		<Card
			className="common-card card-normal-padding"
			sx={{ backgroundColor: SecondaryColor.SECONDARY_LV10 }}
		>
			<div className="profile-page-user-info">
				{avatarPlaceholder}
				<div style={{ color: "white" }}>
					<div className="typography-heading-01">{`${user?.firstName} ${user?.lastName}`}</div>
					<div className="typography-caption">{user?.userName}</div>
					<div className="typography-caption">
						{getUserRoleString(user?.role?.roleKey as SystemRole, translation)}
					</div>
				</div>
			</div>
		</Card>
	);

	let profileTabs: Array<{
		name: string;
		description?: string;
		value: any;
		children?: React.ReactNode;
		key: string;
	}> = [
		{
			name: translation(FrontendProfileTab.PROFILE),
			value: FrontendProfileTab.PROFILE,
			key: FrontendProfileTab.PROFILE,
			children: (
				<div
					className={`center-text profile-page-radio-option  ${
						currentTab === FrontendProfileTab.PROFILE
							? "profile-page-radio-option-selected"
							: ""
					}`}
				>
					{translation(FrontendProfileTab.PROFILE)}
				</div>
			),
		},
	];

	const chooseProfileTab: React.ReactNode = (
		<Card className=" card-border-radius-14p card-small-padding profile-page-tab-selection-card ">
			<CustomRadioGroup
				options={profileTabs}
				selectedOption={currentTab}
				onChange={(e) => setCurrentTab(e.target.value)}
				className="profile-page-radio-group"
			/>
		</Card>
	);
	const actionPanel: React.ReactNode = (
		<div
			className="profile-page-action-panel"
			style={{
				visibility:
					currentTab === FrontendProfileTab.SPECIFICATIONS
						? "hidden"
						: "initial",
			}}
		>
			<OutlinedButton
				key="discard-changes-button"
				id="discard-changes-button"
				children={translation("DiscardChanges")}
				disabled={loading}
				onClick={() => {
					discardChanges();
				}}
				outlineColor={NeutralColor.NEUTRAL_GREY_950}
			></OutlinedButton>

			<GreenButton
				key="save-changes-button"
				id="save-changes-button"
				children={
					loading ? (
						<ButtonTextLoadingAnimation
							text={translation("Loading")}
						></ButtonTextLoadingAnimation>
					) : (
						translation("SaveChanges")
					)
				}
				disabled={loading}
				onClick={() => {
					handleSubmitUserInfo();
				}}
			></GreenButton>
		</div>
	);

	const actionRow: React.ReactNode = (
		<div className="profile-page-action-row">
			{chooseProfileTab}
			{actionPanel}
		</div>
	);

	const clientInformation: React.ReactNode = (
		<UserClientInfoSection
			clientInformationFields={clientInformationFields}
			// enableNotificationEmail={enableNotificationEmail}
			// setEnableNotificationEmail={setEnableNotificationEmail}
			setChangePasswordModalOpen={setChangePasswordModalOpen}
			setUnsavedChanges={setUnsavedChanges}
		/>
	);

	let information: React.ReactNode = <></>;
	switch (currentTab) {
		case FrontendProfileTab.PROFILE:
			information = clientInformation;
			break;
	}

	const informationCard: React.ReactNode = (
		<Card className="common-card card-normal-padding">{information}</Card>
	);
	return (
		<div style={{ margin: "0 auto" }}>
			{headerCard}
			{actionRow}
			{informationCard}
			<ChangePasswordModal
				userName={user?.userName as string}
				open={changePasswordModalOpen}
				setOpen={setChangePasswordModalOpen}
			></ChangePasswordModal>
		</div>
	);
}
